import React, { useState, useRef, useEffect } from "react";
import {
  FaCloudUploadAlt,
  FaFilePdf,
  FaTimes,
  FaCheck,
  FaUpload,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import { IconButton } from "../UI/DesignStandards/Buttons";

// File name sanitization function
const sanitizeFileName = (fileName) => {
  if (!fileName) return "";
  return fileName
    .replace(/[^a-zA-Z0-9.-]/g, "_") // Replace invalid characters with underscores
    .replace(/_{2,}/g, "_") // Replace multiple underscores with single
    .replace(/^_+|_+$/g, "") // Remove leading/trailing underscores
    .substring(0, 100); // Limit length
};

const FileUpload = ({
  tournamentId,
  onUploadComplete,
  onUploadError,
  maxSize = 2 * 1024 * 1024, // 2MB default
  acceptedFileTypes = [".pdf"],
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragReject, setIsDragReject] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef(null);

  // Detect mobile/touch devices
  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isTouchDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const validateFile = (file) => {
    // Validate file type
    if (!file.type.includes("pdf")) {
      onUploadError("Only PDF files are allowed");
      return false;
    }

    // Validate file size
    if (file.size > maxSize) {
      onUploadError(
        `File size (${(file.size / 1024 / 1024).toFixed(
          2
        )}MB) exceeds the maximum allowed size of ${(
          maxSize /
          1024 /
          1024
        ).toFixed(0)}MB`
      );
      return false;
    }

    return true;
  };

  const uploadFile = async (file) => {
    if (!validateFile(file)) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique filename with sanitization
      const timestamp = new Date().getTime();
      const sanitizedFileName = sanitizeFileName(file.name);
      const fileName = `${tournamentId}/${timestamp}_${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from("tournament-documents")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("tournament-documents").getPublicUrl(fileName);

      setUploadedFile({
        name: sanitizedFileName,
        size: file.size,
        url: publicUrl,
        path: fileName,
      });

      setUploadProgress(100);

      // Call completion callback with sanitized data
      onUploadComplete({
        fileName: sanitizedFileName,
        fileSize: file.size,
        url: publicUrl,
        path: fileName,
      });
    } catch (error) {
      console.error("Upload error:", error);
      onUploadError(error.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
    setIsDragReject(false);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    // Only set drag over to false if we're leaving the drop zone entirely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
      setIsDragReject(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    setIsDragReject(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];

      // Check if it's a PDF
      if (!file.type.includes("pdf")) {
        setIsDragReject(true);
        onUploadError("Only PDF files are allowed");
        return;
      }

      uploadFile(file);
    }
  };

  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
        capture={isMobile ? undefined : undefined} // Don't use camera on mobile for PDFs
      />

      {/* Upload Area */}
      {!uploadedFile && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 md:p-6 text-center cursor-pointer transition-colors ${
            isDragOver
              ? isDragReject
                ? "border-red-400 bg-red-50"
                : "border-brand-charcoal/20 bg-brand-cream"
              : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
          } ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {uploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold mx-auto"></div>
              <p className="text-sm text-gray-600">Uploading...</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-brand-charcoal h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">{uploadProgress}%</p>
            </div>
          ) : (
            <div className="space-y-2">
              <FaCloudUploadAlt className="text-3xl md:text-4xl text-gray-400 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {isMobile
                    ? "Tap to select a PDF file"
                    : isDragOver
                    ? isDragReject
                      ? "Only PDF files are allowed"
                      : "Drop the PDF here"
                    : "Drag & drop a PDF file here, or click to select"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size: {formatFileSize(maxSize)}
                </p>
                {isDragReject && (
                  <p className="text-xs text-red-500 mt-1">
                    Only PDF files are allowed
                  </p>
                )}
                {isMobile && (
                  <p className="text-xs text-brand-gold-ink mt-1">
                    Tap anywhere in this area to browse files
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Uploaded File Display */}
      {uploadedFile && (
        <div className="card-inset p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaFilePdf className="text-brand-charcoal text-xl" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {uploadedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(uploadedFile.size)}
                </p>
              </div>
              <FaCheck className="text-brand-charcoal" />
            </div>
            <IconButton
              onClick={removeFile}
              icon={FaTimes}
              variant="secondary"
              size="sm"
              className="text-gray-400 hover:text-gray-600"
              title="Remove file"
              aria-label="Remove uploaded file"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
